import type { SavePaymentMethodFn } from '@/features/billing'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { BillingMethodListItemViewModel } from '@/features/billing/payment-methods/BillingMethodListItemViewModel'
import { DeleteBillingMethodDialogViewModel } from '@/features/billing/payment-methods/DeleteBillingMethodDialog/DeleteBillingMethodDialogViewModel'
import { type Member } from '@/features/iam/members/Member'
import { type Team } from '@/features/iam/teams/Team'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'

/**
 * Payment method selector view model.
 */
export class PaymentMethodSelectorViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Confirms deleting a payment method.
   */
  readonly deleteDialogVm

  /**
   * The available payment methods.
   */
  readonly methods

  /**
   * The selected payment method, if any.
   */
  @observable accessor selectedPaymentMethod: BillingMethod | null

  /**
   * @param _member the member whose payment methods to select from.
   * @param _team the team whose payment methods to select from, if any.
   * @param initialPaymentMethod the method selected initially.
   * @param _onSave called when a method is selected. Allows the consumer to
   *  save a selected method before the selector is closed.
   * @param _newPaymentMethod starts the New Payment Method flow, until the
   *  signal aborts.
   * @param _onPaymentMethodSelected called once a method is selected.
   */
  constructor(
    private readonly _member: Member,
    private readonly _team: Team | null,
    initialPaymentMethod: BillingMethod | null,
    private readonly _onSave: SavePaymentMethodFn | null,
    private readonly _newPaymentMethod: (
      signal: AbortSignal,
    ) => Promise<BillingMethod | null>,
    private readonly _billingProfileStore: BillingProfileStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _onPaymentMethodSelected: (method: BillingMethod) => void,
  ) {
    this.selectedPaymentMethod = initialPaymentMethod
    this.deleteDialogVm = new DeleteBillingMethodDialogViewModel(
      this._billingProfileStore,
      this._createAlertErrorHandler,
    )
    this.methods = new ViewModelList({
      models: () => this.paymentMethods,
      create: (method) =>
        new BillingMethodListItemViewModel(
          method,
          this._billingProfileStore,
          this.deleteDialogVm,
          this._createAlertErrorHandler,
          {
            team: this._team,
            select: this.selectMethod,
            isSelected: (m) => this.selectedPaymentMethod === m,
            isSelecting: (m) =>
              this.selectMethod.pending && this.selectMethod.args[0] === m,
          },
        ),
    })
    this._lifecycle.manage(this.methods)
  }

  /**
   * The available billing methods.
   */
  @computed
  get paymentMethods() {
    return this._team
      ? this._billingProfileStore.billingMethodsForTeam(this._team)
      : this._billingProfileStore.billingMethodsForIndividual(this._member)
  }

  /**
   * Whether the user may add and delete methods.
   */
  @computed
  get canManagePaymentMethods() {
    const profile = this._team
      ? this._billingProfileStore.teamBillingProfile(this._team)
      : this._billingProfileStore.individualBillingProfile(this._member)
    return profile ? this._billingProfileStore.canManage(profile) : false
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The view renders a failed load from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this.deleteDialogVm.modal.close()
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the billing info.
   */
  fetch = task(async (signal: AbortSignal) => {
    await (this._team
      ? this._billingProfileStore.fetchBillingProfileForTeam(this._team.id)
      : this._billingProfileStore.fetchBillingProfileForIndividual(
          this._member.workspace.id,
          this._member.user.publicId,
        ))
    signal.throwIfAborted()
    // Show the new payment method modal immediately if there
    // are no payment methods.
    if (this.paymentMethods.length === 0) {
      // Selecting the new method alerts its own failure.
      void this.newPaymentMethod().catch(() => undefined)
    }
  })

  /**
   * Selects a Billing Method.
   */
  selectMethod = task.resolved(async (method: BillingMethod) => {
    await this._onSave?.(method).catch(
      this._createAlertErrorHandler('Selecting the payment method failed'),
    )
    this._onSelected(method)
  })

  /**
   * Starts the New Payment Method flow.
   */
  newPaymentMethod = task.resolved(async () => {
    const method = await this._newPaymentMethod(this._lifecycle.signal)

    if (!method) {
      return
    }

    await this.selectMethod(method)
  })

  /**
   * Called when a billing method is selected.
   * @param method
   */
  private _onSelected(method: BillingMethod) {
    this._setMethod(method)
    this._onPaymentMethodSelected(method)
  }

  /**
   * Sets the method.
   *
   * @param method
   */
  @action.bound
  private _setMethod(method: BillingMethod | null) {
    this.selectedPaymentMethod = method
  }
}
